/**
 * What the Notes editor can do, declared once.
 *
 * Kept apart from the React component so the toolbar's contents, the font
 * list, and the block styles are data a test can read — and so adding a
 * button is a line here rather than a change to markup.
 */

export interface FontChoice {
  id: string;
  label: string;
  /** Written into the document as a fontFamily mark. */
  stack: string;
}

/**
 * Four families, each a full stack.
 *
 * A bare 'Georgia' is a font somebody's Linux machine does not have, and the
 * browser silently substitutes something with different metrics — so a note
 * written on a Mac reads differently elsewhere. Each entry ends in a generic.
 */
export const FONT_CHOICES: FontChoice[] = [
  {
    id: 'sans',
    label: 'Sans Serif',
    stack: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  { id: 'display', label: 'Montserrat', stack: "Montserrat, 'Google Sans', Inter, sans-serif" },
  { id: 'serif', label: 'Serif', stack: "Georgia, 'Times New Roman', Times, serif" },
  { id: 'mono', label: 'Monospace', stack: "'SF Mono', Menlo, Consolas, 'Courier New', monospace" },
];

export type BlockStyleId = 'title' | 'heading' | 'subheading' | 'body';

export interface BlockStyle {
  id: BlockStyleId;
  label: string;
  /** null means paragraph. */
  level: 1 | 2 | 3 | null;
}

export const BLOCK_STYLES: BlockStyle[] = [
  { id: 'title', label: 'Title', level: 1 },
  { id: 'heading', label: 'Heading', level: 2 },
  { id: 'subheading', label: 'Subheading', level: 3 },
  { id: 'body', label: 'Body text', level: null },
];

export type MarkId = 'bold' | 'italic' | 'underline' | 'strike';

export interface MarkButton {
  id: MarkId;
  label: string;
  /** What the button shows. */
  glyph: string;
  shortcutHint: string;
}

export const MARK_BUTTONS: MarkButton[] = [
  { id: 'bold', label: 'Bold', glyph: 'B', shortcutHint: 'Cmd+B' },
  { id: 'italic', label: 'Italic', glyph: 'I', shortcutHint: 'Cmd+I' },
  { id: 'underline', label: 'Underline', glyph: 'U', shortcutHint: 'Cmd+U' },
  { id: 'strike', label: 'Strikethrough', glyph: 'S', shortcutHint: 'Cmd+Shift+X' },
];

export type ListId = 'bulletList' | 'orderedList' | 'taskList';

export interface ListButton {
  id: ListId;
  label: string;
  glyph: string;
}

export const LIST_BUTTONS: ListButton[] = [
  { id: 'bulletList', label: 'Bullet list', glyph: '•' },
  { id: 'orderedList', label: 'Numbered list', glyph: '1.' },
  { id: 'taskList', label: 'Checklist', glyph: '☑' },
];

/**
 * Keyboard shortcuts the PAGE owns, as opposed to the ones the editor owns.
 *
 * Bold and italic belong to the editor and are handled by its own keymap.
 * These four are about the app around it, so they are listed here and bound
 * once — and written down so the help text and the handler cannot drift.
 *
 * Cmd+F is deliberately taken over. The browser's own find searches the
 * rendered page, which on this screen is one note; the person almost always
 * means "find a note".
 */
export interface PageShortcut {
  id: 'newNote' | 'checklist' | 'search' | 'focus';
  label: string;
  key: string;
  shift: boolean;
  /** Cmd on a Mac, Ctrl elsewhere. */
  meta: true;
}

export const PAGE_SHORTCUTS: PageShortcut[] = [
  { id: 'newNote', label: 'New note', key: 'n', shift: false, meta: true },
  { id: 'checklist', label: 'Checklist item', key: 'l', shift: true, meta: true },
  { id: 'search', label: 'Search notes', key: 'f', shift: false, meta: true },
  { id: 'focus', label: 'Focus mode', key: 'f', shift: true, meta: true },
];

/** Which shortcut a keystroke is, or null. */
export function shortcutFor(event: {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}): PageShortcut['id'] | null {
  // metaKey OR ctrlKey: Cmd on macOS, Ctrl on Windows and Linux. Accepting
  // both means one binding works everywhere rather than two that drift.
  if (!event.metaKey && !event.ctrlKey) {
    return null;
  }
  const key = event.key.toLowerCase();
  const match = PAGE_SHORTCUTS.find(
    (shortcut) => shortcut.key === key && shortcut.shift === event.shiftKey,
  );
  return match?.id ?? null;
}

export const EXPORT_ACTIONS = [
  { id: 'slack', label: 'Copy for Slack' },
  { id: 'html', label: 'Copy Clean HTML' },
  { id: 'pdf', label: 'Export PDF' },
] as const;

export type ExportActionId = (typeof EXPORT_ACTIONS)[number]['id'];
